import slaydApi from './axiosInstance';
import type { Product } from '@/types/Product.types';

export interface CuratorIgPostListData {
  id: number;
  postId: string;
  postType: 'INSTA_POST' | 'INSTA_REEL';
  postTypeDisplay: string;
  caption?: string;
  imageUrls?: string[];
  videoUrl?: string;
  thumbnailUrl?: string;
  mediaThumbnail?: string;
  postedAt?: string;
  isApproved: boolean | null;
  curatorName?: string;
  curatorImage?: string;
  curatorIgUsername?: string;
  productCount: number;
  curationTitle?: string;
}

export interface CuratorInfo {
  id: number;
  name: string;
  imageUrl?: string;
  igUsername?: string;
  followersCount?: number;
}

export interface MediaVideoInfo {
  url: string;
  thumbnailUrl?: string;
}

export interface CurationInfo {
  id: number;
  title?: string;
  curationImage?: string;
}

export interface CuratorIgPostDetailData {
  id: number;
  postId: string;
  postType: 'INSTA_POST' | 'INSTA_REEL';
  postTypeDisplay: string;
  caption?: string;
  imageUrls?: string[];
  videoUrl?: string;
  thumbnailUrl?: string;
  mediaImages: string[];
  mediaVideo?: MediaVideoInfo;
  postedAt?: string;
  isApproved: boolean | null;
  curator?: CuratorInfo;
  products: Product[];
  curation?: CurationInfo;
}

export interface CuratorIgPostListResponse {
  results: CuratorIgPostListData[];
  pagination: {
    currentPage: number;
    totalPages: number;
    totalItems: number;
    pageSize: number;
    hasNext: boolean;
    hasPrevious: boolean;
  };
}

export type ApprovalFilter = 'all' | 'pending' | 'approved' | 'rejected';

/**
 * Fetch a list of curator IG posts with optional filtering
 */
export async function fetchCuratorPosts(
  filter: ApprovalFilter = 'all',
  page: number = 1,
  pageSize: number = 50,
  curatorName?: string,
  title?: string
): Promise<CuratorIgPostListResponse> {
  try {
    const params: Record<string, string | number> = {
      page,
      page_size: pageSize,
      ordering: '-created_at',
    };

    if (curatorName) {
      params.curator_name = curatorName;
    }

    if (title) {
      params.title = title;
    }

    switch (filter) {
      case 'pending':
        params.is_approved = 'null';
        break;
      case 'approved':
        params.is_approved = 'true';
        break;
      case 'rejected':
        params.is_approved = 'false';
        break;
      // 'all' — no filter
    }

    const response = await slaydApi.get<CuratorIgPostListResponse>(
      '/homepage/curator/posts/',
      { params }
    );
    if (!response.data) throw new Error('Invalid response');
    return response.data;
  } catch (error) {
    console.error(error);
    throw new Error('Error fetching curator posts');
  }
}

/**
 * Fetch a single CuratorIgPost by ID with full details + products
 */
export async function fetchCuratorPostById(
  id: number | string
): Promise<CuratorIgPostDetailData> {
  try {
    const response = await slaydApi.get<CuratorIgPostDetailData>(
      `/homepage/curator/posts/${id}/`
    );
    if (!response.data) throw new Error('Invalid response');
    return response.data;
  } catch (error) {
    console.error(error);
    throw new Error('Error fetching curator post');
  }
}

/**
 * Approve or reject a CuratorIgPost
 */
export async function approveCuratorPost(
  id: number | string,
  isApproved: boolean
): Promise<{ id: number; isApproved: boolean }> {
  try {
    const response = await slaydApi.patch<{ id: number; isApproved: boolean }>(
      `/homepage/curator/posts/${id}/`,
      { is_approved: isApproved }
    );
    if (!response.data) throw new Error('Invalid response');
    return response.data;
  } catch (error) {
    console.error(error);
    throw new Error('Error approving curator post');
  }
}
